/**
 * React Query hooks for the Review Queue page.
 *
 * Separate from useDashboardData.ts because the full Review Queue page
 * needs pagination and a mutation, whereas the Dashboard sidebar uses
 * a simpler read-only hook with a fixed limit.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { dashboardKeys } from '@/hooks/data/useDashboardData';
import { fetchReviewQueue, reviewTrace } from '@/services/kineticService';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const reviewQueueKeys = {
  all: ['review-queue'] as const,
  list: (limit: number, offset: number) =>
    [...reviewQueueKeys.all, 'list', { limit, offset }] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Fetch the paginated review queue. */
export function useReviewQueuePage(limit = 20, offset = 0) {
  return useQuery({
    queryKey: reviewQueueKeys.list(limit, offset),
    queryFn: () => fetchReviewQueue(limit, offset),
  });
}

/** Mutation: approve or reject an IN_REVIEW trace. */
export function useReviewTrace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ traceId, action, rationale }: {
      traceId: string;
      action: 'approve' | 'reject';
      rationale?: string;
    }) => reviewTrace(traceId, action, rationale),
    onSuccess: () => {
      // Invalidate both the full page queue and the dashboard sidebar
      queryClient.invalidateQueries({ queryKey: reviewQueueKeys.all });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}
